import { Accessor, createSignal, untrack } from "solid-js";

import { Participant, Room, RoomEvent } from "livekit-client";

import type { SoundController } from "@revolt/client/Sounds";

import { SavedSound, konkordVoice } from "./konkordVoice";

/**
 * Konkord: data channel topic of the soundboard
 */
const TOPIC = "konkord-soundboard";
/** A sound may be sent this often by one person */
export const SOUNDBOARD_COOLDOWN_MS = 3000;
/** Anything sooner from the same person is ignored by whoever hears it */
const RECEIVE_COOLDOWN_MS = 1500;
/** Sounds playing at once, newer ones are skipped */
const MAX_PLAYING = 3;
/** Longest a sound plays */
const MAX_PLAY_MS = 8000;
/** Biggest file one can save (the server takes up to 300 KB) */
export const MAX_SOUND_FILE_BYTES = 300_000;
/** Longest sound one can save */
export const MAX_SOUND_SECONDS = 6;

/**
 * Konkord: sounds that come with Konkord (CC0, from Freesound; credits in
 * public/soundboard/CREDITS.md)
 */
export const BUILTIN_SOUNDS: { id: string; name: string; icon: string }[] = [
  { id: "buzina", name: "Buzina", icon: "campaign" },
  { id: "ba-dum-tss", name: "Ba dum tss", icon: "sentiment_very_satisfied" },
  {
    id: "trombone-triste",
    name: "Trombone triste",
    icon: "sentiment_dissatisfied",
  },
  { id: "fracasso", name: "Fracasso", icon: "thumb_down" },
  { id: "errou", name: "Errou", icon: "close" },
  { id: "aplausos", name: "Aplausos", icon: "celebration" },
  { id: "rufar", name: "Rufar de tambores", icon: "music_note" },
  { id: "arranhao-de-disco", name: "Arranhão de disco", icon: "album" },
  { id: "grilos", name: "Grilos", icon: "bug_report" },
  { id: "caixa-registradora", name: "Caixa registradora", icon: "payments" },
  { id: "boing", name: "Boing", icon: "sports_basketball" },
  { id: "boom", name: "Boom", icon: "bolt" },
  { id: "peido", name: "Peido", icon: "air" },
];
const BUILTIN_IDS = new Set(BUILTIN_SOUNDS.map((s) => s.id));

/**
 * Konkord: a sound sent to the call (a built-in one, or one someone saved)
 */
export type SoundboardSound =
  | { kind: "builtin"; id: string }
  | { kind: "saved"; owner: string; id: string };

const SAVED_ID = /^[A-Za-z0-9_-]{16}$/;
const USER_ID = /^[0-9A-HJKMNP-TV-Z]{26}$/;

function parse(payload: Uint8Array): SoundboardSound | undefined {
  try {
    const data = JSON.parse(new TextDecoder().decode(payload));
    if (data?.kind === "builtin" && BUILTIN_IDS.has(data.id))
      return { kind: "builtin", id: data.id };
    if (
      data?.kind === "saved" &&
      typeof data.owner === "string" &&
      USER_ID.test(data.owner) &&
      typeof data.id === "string" &&
      SAVED_ID.test(data.id)
    )
      return { kind: "saved", owner: data.owner, id: data.id };
  } catch {
    // not ours
  }
}

/**
 * Konkord: where a sound is played from
 */
export function soundboardUrl(sound: SoundboardSound) {
  return sound.kind === "builtin"
    ? `/soundboard/${sound.id}.ogg`
    : konkordVoice.soundUrl(sound.owner, sound.id);
}

/**
 * Konkord: the soundboard of the call we're in. Clicking a sound tells
 * everyone in the call (data channel) and each one plays the file on their
 * own, at their sounds' volume, unless they turned the soundboard off.
 */
class Soundboard {
  /** The tray is open */
  open: Accessor<boolean>;
  setOpen: (open: boolean) => void;

  /** Sounds saved by each person in the call (us included) */
  saved: Accessor<Record<string, SavedSound[]>>;
  #setSaved: (saved: Record<string, SavedSound[]>) => void;

  /** When we may send the next sound */
  readyAt: Accessor<number>;
  #setReadyAt: (at: number) => void;

  #room?: Room;
  #me = "";
  #sound?: SoundController;
  #enabled: () => boolean = () => true;
  #unbind?: () => void;
  #lastFrom = new Map<string, number>();
  #playing = new Set<HTMLAudioElement>();

  constructor() {
    const [open, setOpen] = createSignal(false);
    this.open = open;
    this.setOpen = setOpen;
    const [saved, setSaved] = createSignal<Record<string, SavedSound[]>>({});
    this.saved = saved;
    this.#setSaved = setSaved;
    const [readyAt, setReadyAt] = createSignal(0);
    this.readyAt = readyAt;
    this.#setReadyAt = setReadyAt;
  }

  /**
   * Follow the call we're in (undefined when we left it)
   */
  bind(
    room: Room | undefined,
    me: string | undefined,
    sound: SoundController,
    enabled: () => boolean,
  ) {
    this.#unbind?.();
    this.#unbind = undefined;
    this.#room = undefined;
    if (!room || !me) {
      this.setOpen(false);
      this.#setSaved({});
      return;
    }

    this.#room = room;
    this.#me = me;
    this.#sound = sound;
    this.#enabled = enabled;

    const onData = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic === TOPIC && participant)
        this.#receive(payload, participant.identity);
    };
    const onJoin = (participant: Participant) =>
      void this.refresh([participant.identity]);
    const onLeave = (participant: Participant) => {
      const saved = { ...untrack(this.saved) };
      delete saved[participant.identity];
      this.#setSaved(saved);
    };

    room.on(RoomEvent.DataReceived, onData);
    room.on(RoomEvent.ParticipantConnected, onJoin);
    room.on(RoomEvent.ParticipantDisconnected, onLeave);
    void this.refresh();

    this.#unbind = () => {
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.ParticipantConnected, onJoin);
      room.off(RoomEvent.ParticipantDisconnected, onLeave);
    };
  }

  /**
   * Load the saved sounds of these people (everyone in the call by default)
   */
  async refresh(users?: string[]) {
    const room = this.#room;
    if (!room) return;
    const who = users ?? [
      this.#me,
      ...[...room.remoteParticipants.values()].map((p) => p.identity),
    ];
    try {
      const found = await konkordVoice.listSounds(
        who.filter((id) => USER_ID.test(id)),
      );
      if (this.#room !== room) return;
      this.#setSaved({ ...untrack(this.saved), ...found });
    } catch (err) {
      console.error("[konkord] couldn't load the soundboard", err);
    }
  }

  /**
   * Play a sound for everyone in the call; false while cooling down
   */
  async send(sound: SoundboardSound) {
    const room = this.#room;
    if (!room || Date.now() < untrack(this.readyAt)) return false;
    this.#setReadyAt(Date.now() + SOUNDBOARD_COOLDOWN_MS);
    this.#play(sound);
    await room.localParticipant
      .publishData(new TextEncoder().encode(JSON.stringify(sound)), {
        reliable: true,
        topic: TOPIC,
      })
      .catch((err) => console.error("[konkord] soundboard send failed", err));
    return true;
  }

  /**
   * Hear one of the sounds alone, without sending it
   */
  preview(sound: SoundboardSound) {
    const url = soundboardUrl(sound);
    if (url) this.#sound?.playUrl(url);
  }

  /**
   * Save a new sound of ours (audio file as base64)
   */
  async add(name: string, audio: string) {
    const sound = await konkordVoice.addSound(name, audio);
    const saved = untrack(this.saved);
    this.#setSaved({
      ...saved,
      [this.#me]: [...(saved[this.#me] ?? []), sound],
    });
    return sound;
  }

  /**
   * Delete one of our sounds
   */
  async remove(id: string) {
    await konkordVoice.removeSound(id);
    const saved = untrack(this.saved);
    this.#setSaved({
      ...saved,
      [this.#me]: (saved[this.#me] ?? []).filter((s) => s.id !== id),
    });
  }

  #receive(payload: Uint8Array, from: string) {
    const sound = parse(payload);
    if (!sound) return;
    // someone hammering the button is heard once in a while
    const last = this.#lastFrom.get(from) ?? 0;
    if (Date.now() - last < RECEIVE_COOLDOWN_MS) return;
    this.#lastFrom.set(from, Date.now());
    this.#play(sound);
  }

  #play(sound: SoundboardSound) {
    if (!this.#sound || !this.#enabled()) return;
    if (this.#playing.size >= MAX_PLAYING) return;
    const url = soundboardUrl(sound);
    if (!url) return;
    const audio = this.#sound.playUrl(url);
    this.#playing.add(audio);
    const done = () => {
      clearTimeout(timer);
      audio.pause();
      this.#playing.delete(audio);
    };
    const timer = setTimeout(done, MAX_PLAY_MS);
    audio.addEventListener("ended", done, { once: true });
    audio.addEventListener("error", done, { once: true });
  }
}

export const soundboard = new Soundboard();
